import { FullEventModel } from '../../../shared/models/fullEvent.model';
import { eventTypeEnum } from '../../../shared/enums/eventTypeEnum';
import { InstitutionProfileModel } from '../../../shared/models/institutionProfile.model';
import { HtmlContentModel } from '../../../shared/models/htmlContent.model';
import { EventInviteeModel } from '../../../shared/models/eventInvitee.model';
import { EventInviteeGroupModel } from '../../../shared/models/eventInviteeGroup.model';
import { SimpleGroupModel } from '../../../shared/models/simpleGroup.model';
import { ResourceModel } from '../../../shared/models/resource.model';
import { AttachmentModel } from '../../../shared/models/attachment.model';
import { RepeatingPatternModel } from '../../../shared/models/repeatingPattern.model';
import { LessonModel } from '../../../shared/models/lesson.model';
import { TimeSlotEventModel } from '../../../shared/models/timeSlotEvent.model';
import { VacationRegistrationModel } from '../../../shared/models/vacationRegistration.model';
import { GroupHomeInviteeModel } from '../../../shared/models/groupHomeInvitee.model';
import upperFirst from 'lodash/upperFirst';
import { DateTimeUtil } from '../../../shared/utils/dateTimeUtil';
import { TimeslotResponseModel } from '../../../shared/models/timeslotResponse.model';
import i18n from '../../../shared/locales/i18n';
import { RepeatingPattern } from './repeatingPattern';
import { resourceTypes } from '../../../shared/enums/resourceTypes';
import { teacherTypes } from '../../../shared/enums/teacherTypes';
import { eventResponseStatus } from '../../../shared/enums/eventResponseStatus';
import { portalRoles } from '../../../shared/enums/portalRoles';
import { dateFormatEnum } from '../../../shared/enums/dateFormatEnum';

export class EventData implements FullEventModel {
  id: number;
  title: string;
  allDay: boolean;
  startDateTime: string;
  endDateTime: string;
  oldStartDateTime: string;
  oldEndDateTime: string;
  responseRequired: boolean;
  private: boolean;
  type: eventTypeEnum;
  addedToInstitutionCalendar: boolean;
  isDeleted: boolean;
  eventClass: string;
  isDeadlineExceeded: boolean;
  hideInOwnCalendar: boolean;
  doRequestNumberOfParticipants: boolean;
  creator: InstitutionProfileModel;
  description: HtmlContentModel;
  pendingMedia: boolean;
  coOrganizers?: EventInviteeModel[];
  invitees?: EventInviteeModel[];
  inviteeGroups?: EventInviteeGroupModel[];
  invitedGroups?: SimpleGroupModel[];
  primaryResource?: ResourceModel;
  primaryResourceText?: string;
  additionalResources?: ResourceModel[];
  additionalResourceText?: string;
  attachments?: AttachmentModel[];
  repeating?: RepeatingPatternModel;
  institutionCode?: string;
  institutionName?: string;
  lesson?: LessonModel;
  timeSlot?: TimeSlotEventModel;
  vacationRegistration?: VacationRegistrationModel;
  responseDeadline?: string;
  invitedGroupHomeChildren?: GroupHomeInviteeModel[];
  canEditStartDate?: boolean;
  isVacationCreatedFromVacationRequest?: boolean;
  actualNumberOfParticipants?: number;
  maximumNumberOfParticipants?: number;
  occurrenceDate?: string;
  canAnswerForSeries?: boolean;
  lastReminderDateTime?: string;

  private readonly regardingInstitutionProfileIds: number[];
  private readonly institutionProfileIds?: number[];
  private readonly groupHomeId?: number;
  private readonly regardingChildrenFromGroupHomeIds: number[];
  private readonly activeOtpInboxIds: number[];
  private readonly selectedDate?: string;

  constructor(
    eventData: FullEventModel,
    regardingInstitutionProfileIds: number[] = [],
    institutionProfileIds: number[] = [],
    groupHomeId: number = null,
    selectedDate: string = null,
    regardingChildrenFromGroupHomeIds: number[] = [],
    activeOtpInboxIds: number[] = []
  ) {
    Object.assign(this, eventData);
    this.institutionProfileIds = institutionProfileIds;
    this.regardingInstitutionProfileIds = regardingInstitutionProfileIds;
    this.groupHomeId = groupHomeId;
    this.selectedDate = selectedDate;
    this.regardingChildrenFromGroupHomeIds = regardingChildrenFromGroupHomeIds;
    this.activeOtpInboxIds = activeOtpInboxIds;
  }

  get canViewDateTime() {
    return !this.hasTimeslots || this.respondedTimeslot != null;
  }

  get regardingProfile() {
    return (
      this.invitees?.find(invitee => this.regardingInstitutionProfileIds?.includes(invitee.instProfile.id))
        ?.instProfile || null
    );
  }

  get isCreator() {
    return this.institutionProfileIds.includes(this.creator?.id);
  }

  get isCoOrganizer() {
    return (
      this.coOrganizers?.some(coOrganizer => this.institutionProfileIds.includes(coOrganizer.instProfile.id)) ?? false
    );
  }

  get isInvitee() {
    const isGroupHomeInvitee =
      this.invitedGroupHomeChildren?.some(invitee => invitee.groupHomeId === this.groupHomeId) || false;
    return this.currentInviteeInstitutionProfile != null || (this.isCoOrganizer && !this.hasTimeslots) || isGroupHomeInvitee;
  }

  // use response() instead, if you want to include groupHome invitee as well
  get currentInviteeInstitutionProfile() {
    const invitees = this.invitees || [];
    const coOrganizers = this.coOrganizers || [];
    if (this.hasTimeslots) {
      return invitees.find(invitee => this.institutionProfileIds.includes(invitee.instProfile.id));
    }
    return [...invitees, ...coOrganizers].find(invitee => this.institutionProfileIds.includes(invitee.instProfile.id));
  }

  get response() {
    const invitees = this.invitees || [];
    const coOrganizers = this.coOrganizers || [];
    const responses = [...invitees, ...coOrganizers];
    const groupHomeInvitees = this.invitedGroupHomeChildren || [];
    return (
      responses.find(response => this.institutionProfileIds.includes(response.instProfile.id)) ||
      this.findMatchingGroupHomeInvitation(groupHomeInvitees, this.groupHomeId, this.regardingChildrenFromGroupHomeIds, this.activeOtpInboxIds)
    );
  }

  get responseType() {
    return this.response?.responseType || null;
  }

  get canRespond() {
    if (this.isDeadlineExceeded) {
      return false;
    }
    if (!this.isInvitee) {
      return false;
    }
    if (this.hasTimeslots) {
      const isChild = this.currentInviteeInstitutionProfile?.instProfile?.role === portalRoles.CHILD;
      return this.regardingProfile != null && !isChild;
    }
    return true;
  }

  get canOrganize() {
    return this.isCreator || this.isCoOrganizer;
  }

  get canAnswerAsTentative() {
    return this.currentInviteeInstitutionProfile?.instProfile?.role === portalRoles.EMPLOYEE;
  }

  get dateTime() {
    const { startTime, endTime } = this.getEventTimeRange();
    if (this.allDay) {
      return upperFirst(DateTimeUtil.formatDateRange(startTime, endTime));
    }
    return upperFirst(DateTimeUtil.formatTimeRange(startTime, endTime));
  }

  get duration() {
    if (this.allDay) {
      return i18n.t('calendar.all_day');
    }
    const { startTime, endTime } = this.getEventTimeRange();
    return DateTimeUtil.calculateDuration(startTime, endTime);
  }

  get oldDateTime() {
    if (!this.oldStartDateTime || !this.oldEndDateTime) {
      return null;
    }
    const timeRange = DateTimeUtil.formatTimeRange(this.oldStartDateTime, this.oldEndDateTime);
    return upperFirst(timeRange);
  }

  get deadline() {
    if (!this.responseDeadline) {
      return null;
    }
    return i18n.t('calendar.response_deadline', {
      date: DateTimeUtil.formatDate(this.responseDeadline, 'ddd. DD. MMM.'),
    });
  }

  get isRepeating() {
    return this.repeating != null;
  }

  get recurrence() {
    if (!this.repeating) {
      return '';
    }

    let endDate = '';
    if (this.repeating.lastOccurrenceDate) {
      endDate = i18n.t('calendar.end_at', {
        endDate: DateTimeUtil.formatDate(this.repeating.lastOccurrenceDate, dateFormatEnum.SHORT_DATE),
      }) as string;
    }

    return RepeatingPattern.getPattern(
      this.repeating.pattern,
      endDate,
      this.repeating.weekdayMask,
      this.repeating.dayInMonth,
      this.repeating.interval,
      this.repeating.occurenceLimit
    );
  }

  get locations() {
    const locations = [];
    if (this.primaryResource && this.primaryResource.displayName) {
      locations.push(this.primaryResource.displayName);
    } else if (this.primaryResourceText) {
      // resource that is not from institution list (lesson)
      locations.push(this.primaryResourceText);
    }
    if (Array.isArray(this.additionalLocations)) {
      locations.push(...this.additionalLocations);
    }
    return locations;
  }

  get additionalLocations() {
    return this.additionalResources
      .filter(location => location.resourceCategory.resourceType === resourceTypes.LOCATION)
      .map(location => location.displayName);
  }

  get freeTextLocation() {
    return this.additionalResourceText;
  }

  get coOrganizerList() {
    return (
      this.coOrganizers
        ?.map(coOrganizer => `${coOrganizer.instProfile.fullName} (${coOrganizer.instProfile.metadata})`)
        .join(', ') ?? ''
    );
  }

  get resources() {
    return this.additionalResources.filter(
      resource => resource.resourceCategory.resourceType !== resourceTypes.LOCATION
    );
  }

  get resourceList() {
    return this.resources.map(resource => resource.displayName).join(', ');
  }

  get isLesson() {
    return this.type === eventTypeEnum.LESSON;
  }

  get seats() {
    return this.maximumNumberOfParticipants || Infinity;
  }

  get emptySeats() {
    const actualNumberOfParticipants = this.actualNumberOfParticipants || 0;
    return Math.max(this.seats - actualNumberOfParticipants, 0);
  }

  get teacher() {
    return (
      this.lesson?.participants.find(participant => participant.participantRole === teacherTypes.PRIMARY)
        ?.participantProfile || null
    );
  }

  get assistance() {
    return (
      this.lesson?.participants.find(participant => participant.participantRole === teacherTypes.HELP)
        ?.participantProfile || null
    );
  }

  get substitute() {
    return (
      this.lesson?.participants.find(participant => participant.participantRole === teacherTypes.SUBSTITUTE)
        ?.participantProfile || null
    );
  }

  get pedagogue() {
    return (
      this.lesson?.participants.find(participant => participant.participantRole === teacherTypes.PEDAGOGUE)
        ?.participantProfile || null
    );
  }

  get invitedGroupList() {
    return this.invitedGroups.map(group => group.name);
  }

  get hasTimeslots() {
    return this.timeSlot != null;
  }

  get isRequiredSeatSelection() {
    return this.doRequestNumberOfParticipants === true;
  }

  get respondedTimeslot(): TimeslotResponseModel {
    const slots = this.timeSlot?.timeSlots || [];
    for (const slot of slots) {
      const answer = slot.answers.find(
        a => a.concerningProfile && a.concerningProfile.profileId === this.regardingProfile?.profileId
      );

      if (answer) {
        const { startTime, endTime } = slot.timeSlotIndexes[answer.selectedTimeSlotIndex];
        return {
          timeslotId: slot.id,
          index: answer.selectedTimeSlotIndex,
          startTime,
          endTime,
        } as TimeslotResponseModel;
      }
    }
    return null;
  }

  get meetingInvitees() {
    return (
      this.invitees?.filter((invitee: EventInviteeModel) =>
        [portalRoles.EMPLOYEE, portalRoles.CHILD].includes(invitee.instProfile.role)
      ) || []
    );
  }

  get canViewRegardingProfile() {
    return this.regardingProfile != null && this.hasTimeslots;
  }

  get numberOfParticipantsPerTimeslot() {
    return this.timeSlot.numberOfParticipantsPerTimeSlot || 1;
  }

  get hasEnoughSlotsForEveryone() {
    if (!this.timeSlot) {
      return true;
    }
    const timeslots = this.timeSlot.timeSlots;
    let numberOfSlots = 0;
    for (const timeslot of timeslots) {
      const blockedAnswers = timeslot.answers.filter(answer => answer.instProfile === null);
      numberOfSlots += timeslot.timeSlotIndexes.length * this.numberOfParticipantsPerTimeslot - blockedAnswers.length;
    }
    return this.meetingInvitees.length <= numberOfSlots;
  }

  private findMatchingGroupHomeInvitation(
    groupHomeInvitees: GroupHomeInviteeModel[],
    groupHomeId: number,
    regardingChildrenFromGroupHomeIds: number[],
    activeOtpInboxIds: number[]): GroupHomeInviteeModel | undefined {
    
    return groupHomeInvitees.find(response => {
      const matchesGroupHome = response.groupHomeId === groupHomeId;
      const matchesOtpInbox = activeOtpInboxIds.includes(response.otpInboxId);
      const matchesChild = regardingChildrenFromGroupHomeIds.includes(response.regardingChildId);

      return matchesGroupHome && matchesOtpInbox && matchesChild;
    });
  }

  getResponseToastMessage(responseType: eventResponseStatus) {
    let messageKey = '';
    switch (responseType) {
      case eventResponseStatus.ACCEPTED:
        messageKey = 'calendar.response_accepted';
        break;
      case eventResponseStatus.TENTATIVE:
        messageKey = 'calendar.response_tentative';
        break;
      case eventResponseStatus.DECLINED:
        messageKey = 'calendar.response_declined';
        break;
      default:
        messageKey = 'calendar.response_changed';
    }
    return i18n.t(messageKey);
  }

  private getEventTimeRange() {
    let startTime = this.startDateTime;
    let endTime = this.endDateTime;
    if (this.respondedTimeslot) {
      startTime = this.respondedTimeslot.startTime;
      endTime = this.respondedTimeslot.endTime;
    }
    if (this.isRepeating && !this.selectedDate) {
      startTime = this.repeating.originalStartDateTime;
      endTime = this.repeating.originalEndDateTime;
    }
    return { startTime, endTime };
  }
}
